import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Smartphone, AlertCircle, HelpCircle } from 'lucide-react';

interface Message {
  id: number;
  text: string;
  sender: 'bot' | 'user';
  timestamp: Date;
}

// Enhanced knowledge base with more questions and answers
const knowledgeBase = [
  {
    keywords: ['hi', 'hello', 'hey', 'good morning', 'good afternoon'],
    response: 'Hello! 👋 Welcome to Angel Cite. I\'m here to help with your agricultural needs. How can I assist you today?'
  },
  {
    keywords: ['order', 'track', 'tracking', 'status', 'my order'],
    response: 'To track your order, please visit "My Account" → "Orders" or share your order number with me. You can also check your email for tracking updates.'
  },
  {
    keywords: ['return', 'refund', 'exchange', 'return policy'],
    response: 'We offer a 7-day return policy for most products. Items must be unused and in original packaging. Visit our Return Policy page for detailed information.'
  },
  {
    keywords: ['shipping', 'delivery', 'ship', 'deliver', 'delivery time'],
    response: 'We offer delivery within Harare (1-2 days) and nationwide (3-5 days). Shipping is FREE for orders over $50. For urgent deliveries, contact our WhatsApp support.'
  },
  {
    keywords: ['product', 'availability', 'stock', 'in stock', 'out of stock'],
    response: 'Our product availability is updated in real-time. If a product shows "Add to Cart", it\'s in stock. For bulk orders, please contact us directly.'
  },
  {
    keywords: ['price', 'cost', 'how much', 'pricing', 'expensive'],
    response: 'All prices are listed on product pages. For wholesale pricing or bulk discounts, please contact our sales team via WhatsApp for personalized quotes.'
  },
  {
    keywords: ['payment', 'pay', 'ecocash', 'credit card', 'cash', 'mobile money'],
    response: 'We accept EcoCash, credit/debit cards, and cash on delivery. EcoCash payments go to +263773520100. Remember to upload your payment confirmation!'
  },
  {
    keywords: ['contact', 'phone', 'email', 'support', 'call', 'reach'],
    response: 'You can reach us at:\n📞 Phone: +263 77 123 4567\n📧 Email: info@angelcite.com\n💬 WhatsApp: +230 5743 2920\n📍 Location: The Avenues, Harare'
  },
  {
    keywords: ['hours', 'open', 'closed', 'operating hours', 'business hours'],
    response: 'We\'re open Monday to Sunday from 8am to 8pm. WhatsApp support is available 24/7 for urgent inquiries.'
  },
  {
    keywords: ['organic', 'quality', 'fresh', 'farm', 'natural', 'pesticide'],
    response: 'All our products are sourced from certified organic farms. We ensure the highest quality and freshness with daily deliveries from local farms.'
  },
  {
    keywords: ['bulk', 'wholesale', 'business', 'reseller', 'restaurant', 'cafe'],
    response: 'We offer wholesale prices for bulk orders and resellers. Please contact our business team via WhatsApp at +230 5743 2920 for custom quotes.'
  },
  {
    keywords: ['thank', 'thanks', 'appreciate', 'thank you'],
    response: 'You\'re welcome! 😊 If you need further assistance, don\'t hesitate to ask. Have a great day!'
  },
  {
    keywords: ['bye', 'goodbye', 'see you', 'exit', 'good night'],
    response: 'Goodbye! 🌱 Thank you for choosing Angel Cite. Remember, you can always reach us on WhatsApp for faster responses.'
  }
];

// Fallback responses when no match is found
const fallbackResponses = [
  "I'm not sure I understand. Could you rephrase your question?",
  "That's an interesting question! Let me connect you with our support team for a more detailed answer.",
  "I'm still learning! For this specific question, please contact our support team who can help you better.",
  "I don't have that information right now. Would you like me to connect you with a human agent?"
];

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: 'Hello! 👋 Welcome to Angel Cite. I\'m here to help with:\n• Order tracking\n• Product information\n• Shipping & delivery\n• Payment methods\n• Return policies\n\nHow can I assist you today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showWhatsAppFallback, setShowWhatsAppFallback] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Handle viewport height changes for mobile devices
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showWhatsAppFallback, isTyping]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = window.innerWidth >= 1024 ? 120 : 80; // Larger on big screens
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  // Find the best response based on user input
  const findResponse = (userInput: string): string => {
    const input = userInput.toLowerCase().trim();
    
    // Check for exact matches in knowledge base
    for (const item of knowledgeBase) {
      if (item.keywords.some(keyword => input.includes(keyword))) {
        return item.response;
      }
    }
    
    // If no match found, increment failed attempts
    setFailedAttempts(prev => prev + 1);
    
    // Return random fallback response
    return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
  };

  // Show WhatsApp fallback after 2 failed attempts
  useEffect(() => {
    if (failedAttempts >= 2 && !showWhatsAppFallback) {
      setShowWhatsAppFallback(true);
      
      // Add WhatsApp fallback message
      setTimeout(() => {
        const fallbackMessage: Message = {
          id: messages.length + 100,
          text: "I'm having trouble answering your questions effectively. Would you like to chat with our human support team on WhatsApp for immediate assistance?",
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, fallbackMessage]);
      }, 1500);
    }
  }, [failedAttempts, showWhatsAppFallback, messages.length]);

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Find and send bot response
    setTimeout(() => {
      const response = findResponse(inputValue);
      const botMessage: Message = {
        id: messages.length + 2,
        text: response,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleWhatsAppRedirect = () => {
    const lastUserMessage = messages.filter(m => m.sender === 'user').pop();
    const message = encodeURIComponent(
      `Chatbot Assistance Request\n\n` +
      `I was asking about: "${lastUserMessage?.text || 'General inquiry'}"\n` +
      `Please assist me with more information.`
    );
    window.open(`https://wa.me/+23057432920?text=${message}`, '_blank');
  };

  const handleResetChat = () => {
    setMessages([
      {
        id: 1,
        text: 'Hello! 👋 Welcome to Angel Cite. I\'m here to help with:\n• Order tracking\n• Product information\n• Shipping & delivery\n• Payment methods\n• Return policies\n\nHow can I assist you today?',
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    setFailedAttempts(0);
    setShowWhatsAppFallback(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Responsive height calculation
  const maxChatHeight = Math.min(
    viewportHeight * 0.75,
    window.innerWidth >= 1024 ? 600 : // Large screens: 600px
    window.innerWidth >= 768 ? 500 :  // Medium screens: 500px
    350                               // Mobile: 350px
  );

  return (
    <div className="fixed bottom-4 right-[80px] z-50 sm:right-4">
      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-14 right-[80px] w-[calc(100vw-2.5rem)] max-w-[280px] sm:absolute sm:bottom-14 sm:right-0 sm:left-auto sm:w-80 md:w-96 lg:w-[450px] lg:max-w-[450px] xl:w-[500px] xl:max-w-[500px] 2xl:w-[550px] 2xl:max-w-[550px] bg-dark-dark dark:bg-white rounded-xl shadow-xl border border-dark-light dark:border-gray-200 overflow-hidden animate-slide-up transition-colors duration-300"
          style={{
            height: `${maxChatHeight}px`,
            maxHeight: window.innerWidth >= 1024 ? '600px' : '550px'
          }}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-accent to-tertiary p-2.5 sm:p-3 md:p-4 lg:p-5 text-white transition-all duration-300 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-xs sm:text-sm md:text-base lg:text-lg truncate">Angel Cite Assistant</h3>
                  <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-white/80 truncate">Ask me anything</p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
                <button
                  onClick={handleResetChat}
                  className="text-[10px] sm:text-xs md:text-sm lg:text-base bg-white/20 hover:bg-white/30 px-1.5 py-0.5 sm:px-2 sm:py-1 md:px-3 md:py-1.5 rounded transition-colors duration-200 whitespace-nowrap"
                  title="Start new conversation"
                >
                  New Chat
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 p-0.5 sm:p-1 md:p-1.5 rounded transition-colors duration-200"
                  title="Close chat"
                >
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages Container with fixed positioning */}
          <div className="flex flex-col h-full">
            {/* Messages scrolling area */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-5 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
              <div className="space-y-1.5 sm:space-y-2 md:space-y-3 lg:space-y-4 pb-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex items-start gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 max-w-[88%] sm:max-w-[85%] md:max-w-[80%] ${
                        message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-8 lg:w-9 lg:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          message.sender === 'bot'
                            ? 'bg-accent dark:bg-primary text-white'
                            : 'bg-tertiary text-white'
                        } transition-colors duration-300 mt-0.5`}
                      >
                        {message.sender === 'bot' ? 
                          <Bot className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4" /> : 
                          <User className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4" />
                        }
                      </div>
                      <div
                        className={`px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 lg:px-5 lg:py-4 rounded-lg ${
                          message.sender === 'bot'
                            ? 'bg-dark dark:bg-white text-gray-200 dark:text-gray-800 rounded-tl-none'
                            : 'bg-tertiary text-white rounded-tr-none'
                        } transition-colors duration-300 break-words`}
                      >
                        <p className="text-xs sm:text-sm md:text-base lg:text-lg whitespace-pre-line leading-relaxed">{message.text}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 max-w-[88%] sm:max-w-[85%] md:max-w-[80%]">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-8 lg:w-9 lg:h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-accent dark:bg-primary text-white transition-colors duration-300 mt-0.5">
                        <Bot className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4" />
                      </div>
                      <div className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 lg:px-5 lg:py-4 rounded-lg bg-dark dark:bg-white text-gray-200 dark:text-gray-800 rounded-tl-none transition-colors duration-300">
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 bg-current rounded-full animate-pulse"></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 bg-current rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 bg-current rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* WhatsApp Fallback */}
                {showWhatsAppFallback && (
                  <div className="animate-fade-in mt-1.5 sm:mt-2 md:mt-3 lg:mt-4">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded p-2 sm:p-3 md:p-4 lg:p-5">
                      <div className="flex items-start gap-1.5 sm:gap-2 md:gap-3 lg:gap-4">
                        <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs sm:text-sm md:text-base lg:text-lg font-medium mb-0.5 truncate">
                            Need Human Assistance?
                          </p>
                          <p className="text-green-100 text-[10px] sm:text-xs md:text-sm lg:text-base mb-1.5">
                            Our support team is ready to help you on WhatsApp.
                          </p>
                          <button
                            onClick={handleWhatsAppRedirect}
                            className="flex items-center justify-center gap-1 sm:gap-2 md:gap-3 w-full bg-white text-green-600 hover:bg-green-50 px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 lg:px-5 lg:py-4 rounded font-semibold text-xs sm:text-sm md:text-base transition-colors duration-200"
                          >
                            <Smartphone className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                            <span className="truncate">Continue on WhatsApp</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Help Hint */}
                {messages.length === 1 && (
                  <div className="text-center pt-1 sm:pt-2 md:pt-3 lg:pt-4">
                    <p className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-400 dark:text-gray-500 px-1">
                      Try asking about: orders, shipping, payments, or products
                    </p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input Area - Now sticky at bottom */}
            <div className="sticky bottom-0 p-2 sm:p-3 md:p-4 lg:p-5 border-t border-dark-light dark:border-gray-100 bg-dark-dark dark:bg-white transition-colors duration-300">
              {/* Input Field */}
              <div className="flex items-end gap-1.5 sm:gap-2 md:gap-3 lg:gap-4">
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type your message here..."
                    rows={1}
                    className="w-full px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3 lg:px-5 lg:py-4 bg-dark-light dark:bg-gray-100 border-0 rounded-lg sm:rounded-xl md:rounded-2xl focus:outline-none focus:ring-1 sm:focus:ring-2 md:focus:ring-3 focus:ring-accent dark:focus:ring-primary text-xs sm:text-sm md:text-base lg:text-lg text-gray-200 dark:text-gray-800 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300 resize-none overflow-y-auto"
                    style={{ minHeight: '36px', maxHeight: window.innerWidth >= 1024 ? '120px' : '80px' }}
                  />
                  <div className="absolute right-1.5 sm:right-2 md:right-3 lg:right-4 bottom-1.5 sm:bottom-2 md:bottom-3 lg:bottom-4">
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim() || isTyping}
                      className={`p-1 sm:p-1.5 md:p-2 lg:p-2.5 rounded-full transition-colors duration-300 ${
                        inputValue.trim() && !isTyping
                          ? 'bg-accent dark:bg-primary text-white hover:bg-accent-dark dark:hover:bg-primary-dark'
                          : 'bg-gray-400 dark:bg-gray-300 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Send className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Help Links */}
              <div className="flex items-center justify-between gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 mt-2 sm:mt-3 md:mt-4 lg:mt-5">
                <div className="flex items-center gap-1">
                  <HelpCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-400 dark:text-gray-500 truncate">Ask about orders, shipping, payments</span>
                </div>
                <a
                  href="https://wa.me/+23057432920"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] sm:text-xs md:text-sm lg:text-base text-accent dark:text-primary hover:underline transition-colors duration-300 whitespace-nowrap"
                >
                  Direct WhatsApp →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 sm:p-4 md:p-5 lg:p-6 rounded-full shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary focus:ring-offset-2 touch-manipulation ${
          isOpen ? 'bg-gray-600' : 'bg-gradient-to-r from-accent to-tertiary'
        } text-white relative`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <X className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8" />
        ) : (
          <>
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8" />
            {failedAttempts >= 2 && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 bg-red-500 rounded-full animate-pulse border border-white"></span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

export default ChatBot;